/**
 * Lecture des horaires d'ouverture, et état courant du restaurant.
 *
 * ## Pourquoi une fonction et pas une phrase écrite à la main
 *
 * Le panneau des horaires affichait deux lignes dans une boîte de 380 px. Ce qu'il lui manquait
 * n'était pas de la décoration mais une information : un visiteur qui lit des horaires veut
 * savoir si c'est ouvert **maintenant**. Elle se déduit entièrement des données déjà présentes
 * dans `openningHours.json` — rien n'est inventé, et rien n'est à saisir en plus.
 *
 * ## Ce que le format impose
 *
 * Une entrée porte un libellé français — « Lundi-Vendredi », « Samedi-Dimanche », ou un jour
 * seul — et deux heures. Le libellé est donc à interpréter, ce qui est le prix de garder le
 * fichier lisible par le restaurateur.
 *
 * Une plage peut **franchir minuit** : `08:00 → 02:00` ferme le lendemain. C'est le cas de ce
 * restaurant, et le manquer ferait afficher « fermé » à une heure du matin alors que la salle
 * est pleine.
 */
export interface OpeningRange {
    readonly label: string;
    readonly horaires: readonly string[];
}

/** Lundi en tête, comme un calendrier français — et comme les libellés du fichier. */
export const JOURS = [
    "lundi",
    "mardi",
    "mercredi",
    "jeudi",
    "vendredi",
    "samedi",
    "dimanche",
] as const;

/** `Date.getDay()` place dimanche en 0 ; nos index placent lundi en 0. */
export const jourDeLaSemaine = (date: Date): number => (date.getDay() + 6) % 7;

const sansAccent = (valeur: string): string =>
    valeur
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .trim()
        .toLowerCase();

const indexJour = (nom: string): number =>
    JOURS.findIndex((jour) => sansAccent(jour) === sansAccent(nom));

/**
 * Les jours couverts par un libellé, dans l'ordre.
 *
 * « Vendredi-Lundi » est accepté et repasse par le week-end : un restaurant fermé en début de
 * semaine l'écrit ainsi, et refuser ce cas obligerait à couper l'entrée en deux.
 */
export const joursDuLabel = (label: string): number[] => {
    const bornes = label.split("-").map((partie) => indexJour(partie));
    if (bornes.some((borne) => borne < 0) || bornes.length === 0 || bornes.length > 2) return [];
    const [debut, fin = bornes[0]] = bornes;

    const jours: number[] = [];
    for (let index = 0; index < JOURS.length; index += 1) {
        const jour = (debut + index) % JOURS.length;
        jours.push(jour);
        if (jour === fin) break;
    }
    return jours;
};

/** Minutes depuis minuit, ou `null` si ce n'est pas une heure. */
export const enMinutes = (heure: string): number | null => {
    const correspondance = /^(\d{1,2}):(\d{2})$/.exec(heure.trim());
    if (correspondance === null) return null;
    const heures = Number(correspondance[1]);
    const minutes = Number(correspondance[2]);
    if (heures > 23 || minutes > 59) return null;
    return heures * 60 + minutes;
};

export interface Statut {
    /** Le restaurant sert-il en ce moment ? */
    readonly ouvert: boolean;
    /** Heure de fermeture quand il est ouvert, prochaine ouverture sinon. `null` si indéterminé. */
    readonly heure: string | null;
    /** Jour de la prochaine ouverture, quand il est fermé et que ce n'est pas aujourd'hui. */
    readonly jour: string | null;
}

const MINUTES_PAR_JOUR = 24 * 60;

/** Toutes les plages de la semaine, aplaties en minutes depuis lundi 00:00. */
const plages = (
    ranges: readonly OpeningRange[],
): { readonly debut: number; readonly fin: number; readonly ferme: string }[] => {
    const resultat: { debut: number; fin: number; ferme: string }[] = [];

    for (const range of ranges) {
        const ouverture = enMinutes(range.horaires[0] ?? "");
        const fermeture = enMinutes(range.horaires[1] ?? "");
        if (ouverture === null || fermeture === null) continue;

        for (const jour of joursDuLabel(range.label)) {
            const debut = jour * MINUTES_PAR_JOUR + ouverture;
            // Une fermeture antérieure à l'ouverture appartient au lendemain.
            const duree =
                fermeture > ouverture
                    ? fermeture - ouverture
                    : MINUTES_PAR_JOUR - ouverture + fermeture;
            resultat.push({ debut, fin: debut + duree, ferme: range.horaires[1] ?? "" });
        }
    }

    return resultat.sort((a, b) => a.debut - b.debut);
};

/**
 * L'état à un instant donné.
 *
 * `maintenant` est un paramètre et non `new Date()` pris à l'intérieur : c'est ce qui rend la
 * fonction vérifiable à minuit, un dimanche, ou pendant une plage qui franchit minuit.
 */
export const statutMaintenant = (ranges: readonly OpeningRange[], maintenant: Date): Statut => {
    const semaine = plages(ranges);
    if (semaine.length === 0) return { ouvert: false, heure: null, jour: null };

    const position =
        jourDeLaSemaine(maintenant) * MINUTES_PAR_JOUR +
        maintenant.getHours() * 60 +
        maintenant.getMinutes();
    const TOTAL = 7 * MINUTES_PAR_JOUR;

    // Une plage commencée dimanche soir déborde sur lundi : on la teste aussi une semaine avant.
    for (const plage of semaine) {
        for (const decalage of [0, -TOTAL]) {
            const debut = plage.debut + decalage;
            if (position >= debut && position < plage.fin + decalage) {
                return { ouvert: true, heure: plage.ferme, jour: null };
            }
        }
    }

    // Fermé : la prochaine ouverture est la première plage qui commence après, en repassant par
    // le début de semaine si nécessaire.
    const prochaine =
        semaine.find((plage) => plage.debut > position) ??
        (semaine[0] === undefined ? undefined : { ...semaine[0], debut: semaine[0].debut + TOTAL });
    if (prochaine === undefined) return { ouvert: false, heure: null, jour: null };

    const jourOuverture = Math.floor((prochaine.debut % TOTAL) / MINUTES_PAR_JOUR);
    const minuteOuverture = prochaine.debut % MINUTES_PAR_JOUR;
    const heure = `${String(Math.floor(minuteOuverture / 60)).padStart(2, "0")}:${String(
        minuteOuverture % 60,
    ).padStart(2, "0")}`;

    return {
        ouvert: false,
        heure,
        jour: jourOuverture === jourDeLaSemaine(maintenant) ? null : JOURS[jourOuverture],
    };
};
